import { parseArgsToInputValues } from "./argParsing";

/**
 * Builds the initial form values for a pipeline instance's jobs by parsing
 * each job's CLI args against its module version's input definitions, then
 * overriding any values supplied by the default preset (if a default preset
 * is present).
 * @param {Object[]} jobs
 * @param {Object[]} moduleVersions
 * @param {Object} [defaultPreset]
 * @returns {Object<string, {
 *   main: Object<string, any>,
 *   preExec?: {
 *     trainedModelVersion?: null | Object,
 *     anotherDateSelection?: Object<string, null | Object>
 *   }
 * }>} An object keyed by job name. Each value has a `main` object mapping
 *   input keys to their resolved values. Jobs with `trainedModelFilters`
 *   also get a `preExec.trainedModelVersion` slot (the full
 *   `TrainedModelVersion` object once selected), initialized to null; the
 *   actual selection is resolved later by `TrainedModelSelector` once its
 *   fetch resolves. Jobs with `anotherDateSelection` get a
 *   `preExec.anotherDateSelection` slot, one null entry per input,
 *   filled in by `AnotherDateSelector`.
 */
export function buildInitialFormValues(jobs, moduleVersions, defaultPreset) {
    const result = {};

    jobs.forEach((job) => {
        // Get the module version of the job
        const moduleVersion = moduleVersions.find(
            (mv) => mv.uuid === job.mainModuleVersionUuid
        );
        if (!moduleVersion)
            throw new Error(
                `Module version not found: ${job.mainModuleVersionUuid}`
            );

        // Deep-copy module version inputs and update their values with the parsed
        // result of the job's CLI args.
        const parsedInputs = parseArgsToInputValues(
            moduleVersion.definition.inputs,
            job.main.args
        );

        // Override with values from the default preset if a default preset is present
        const moduleUuid = moduleVersion.moduleUuid;
        const presetValues = defaultPreset?.values[moduleUuid];
        if (presetValues) {
            Object.entries(presetValues).forEach(([key, value]) => {
                const input = parsedInputs[key];
                if (input) input.value = value;
            });
        }

        // For the form values, we only need the value for each input.
        // Numeric-nargs inputs with a null value get pre-shaped as an
        // array of nulls so the form-state shape stays consistent once
        // the N per-element Controllers mount — otherwise RHF's
        // array-indexed registration would silently convert a scalar
        // null into [null, null, ...] on first render of those fields anyway.
        result[job.name] = {
            main: Object.fromEntries(
                Object.entries(parsedInputs).map(([key, input]) => [
                    key,
                    typeof input.nargs === "number" && input.value == null
                        ? Array(input.nargs).fill(null)
                        : input.value,
                ])
            ),
        };

        if (job.trainedModelFilters) {
            result[job.name].preExec = {
                ...result[job.name].preExec,
                trainedModelVersion: null,
            };
        }

        // For jobs whose spec declares `anotherDateSelection` (keyed by
        // input name), add a `preExec.anotherDateSelection` slot with one
        // null entry per input. Each entry later holds the resolved
        // `preExecItem` of the picked mission (or stays null if the user
        // selects no date); `AnotherDateSelector` fills it in and
        // `buildUpdatedTranslatedPipeline` reads it at submit time.
        if (job.anotherDateSelection) {
            result[job.name].preExec = {
                ...result[job.name].preExec,
                anotherDateSelection: Object.fromEntries(
                    Object.keys(job.anotherDateSelection).map((inputKey) => [
                        inputKey,
                        null,
                    ])
                ),
            };
        }
    });

    return result;
}

/**
 * Build an object keyed by job name, where each value is the corresponding
 * ModuleVersion object of the job.
 *
 * @param {Object[]} jobs
 * @param {Object[]} moduleVersions
 * @returns {Object<string, Object>} map from job name to a ModuleVersion
 *   object (see MODEL_PROPTYPES.ModuleVersion)
 */
export function getJobNameToModuleVersionMap(jobs, moduleVersions) {
    const result = {};

    jobs.forEach((job) => {
        const moduleVersion = moduleVersions.find(
            (mv) => mv.uuid === job.mainModuleVersionUuid
        );
        if (!moduleVersion)
            throw new Error(
                `Module version not found: ${job.mainModuleVersionUuid}`
            );

        result[job.name] = moduleVersion;
    });

    return result;
}
